// The typed errors of the Speedweaver API (R64). Api.js's response interceptor turns every failed request into one of these, so callers branch
// on status, code and reason and never on the axios error. That object carries the request's Authorization header, so nothing here keeps it,
// its config, its request or its response.

const textOr = (value, fallback) => (typeof value === 'string' && value !== '' ? value : fallback);

/**
 * Reads a failed response's body into {code, reason, retryAfter, message, kind}. The API answers in five shapes:
 *   E1 {error, message, reason?, retryAfter?}, E2 {error}, hydra {hydra:description}, lexik {code: 401, message} and symfony {detail}.
 * Anything else is 'text' (a string body with something in it) or 'unknown'. `message` is developer text: it is never shown to a user.
 */
export const parseErrorBody = (status, data, headers) => {
    // The seconds to wait, for any shape of body: the body's retryAfter, else the Retry-After header (the API lists it in expose_headers).
    const retryAfter = Number.isInteger(data?.retryAfter) ? data.retryAfter : (parseInt(headers?.['retry-after'], 10) || null);
    const parsed = (kind, code, reason, message) => ({code, reason, retryAfter, message: textOr(message, `HTTP ${status}`), kind});
    // A body that is not an object has no fields to read.
    const body = data !== null && typeof data === 'object' ? data : {};

    if (typeof body.error === 'string' && typeof body.message === 'string') {
        return parsed('e1', body.error, body.reason ?? null, body.message);
    }
    if (typeof body.error === 'string') {
        // E2 has no text of its own, so its code stands in for it.
        return parsed('e2', body.error, null, body.error);
    }
    if (typeof body['hydra:description'] === 'string') {
        return parsed('hydra', null, null, body['hydra:description']);
    }
    // A lexik body's `code` is the status, not an API error code, so the code stays null.
    if (status === 401 && body.code === 401) {
        return parsed('lexik', null, null, body.message);
    }
    if (typeof body.detail === 'string') {
        return parsed('symfony', null, null, body.detail);
    }
    // A browser hands over '' for a response with no body, so a blank string is unknown, as a missing body is.
    if (typeof data === 'string' && data.trim() !== '') {
        return parsed('text', null, null, data);
    }
    return parsed('unknown', null, null, null);
};

export class ApiError extends Error {
    constructor({status, code = null, reason = null, retryAfter = null, message = '', kind = 'unknown', url = '', method = ''} = {}) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
        this.reason = reason;
        this.retryAfter = retryAfter;
        this.kind = kind;
        this.url = url;
        this.method = method;
    }

    /** An axios error that has a response. Only the parsed fields, the route and the method are kept. */
    static fromResponse(err) {
        const {status, data, headers} = err.response;
        const {url, method} = err.config || {};
        return new ApiError({status, ...parseErrorBody(status, data, headers), url, method});
    }

    is(code) {
        return this.code === code;
    }
}

/** A request that got no response at all: a network failure, a timeout, or an edge answer without CORS headers (nginx or Cloudflare 502, 504, 52x). */
export class NoResponseError extends Error {
    constructor({url = '', method = '', timedOut = false} = {}) {
        super(`No response to ${method} ${url}${timedOut ? ' (timed out)' : ''}`);
        this.name = 'NoResponseError';
        this.url = url;
        this.method = method;
        this.timedOut = timedOut;
    }
}

/**
 * A map the release lacks answers 404 map_not_found, and the editor closes a window of one without a word (R45). The status is part of the test, as it
 * is in the contract: the code alone would also match another status that happens to carry it.
 */
export const isMapNotFound = error => error instanceof ApiError && error.status === 404 && error.is('map_not_found');
